<!--
  @component QueueIO — P3 effecting lens (p-arc).
  chain:   ctx.deployment.dispatch("queue.*", …) → this → ctx.queue
  effects: provision/connect + publish/peek/purge + drain + teardown.
           Queue is ephemeral — drain (NOT restore) on takedown.
-->
<script lang="ts" module>
  import { getContext } from "svelte";
  import { QUEUE_IO_KEY } from "./deployment-io.js";
  import type { QueueIOContext } from "./deployment-io.js";
  export { QUEUE_IO_KEY };
  export type { QueueIOContext };
  export function useQueueIO(): QueueIOContext {
    const ctx = getContext<QueueIOContext | undefined>(QUEUE_IO_KEY);
    if (!ctx) throw new Error("useQueueIO() called outside of a QueueIO tree.");
    return ctx;
  }
</script>
<script lang="ts">
  import { setContext, type Snippet } from "svelte";
  import { useDeploymentIO } from "./DeploymentIO.svelte";
  import type { DeployTargetRef } from "./deployment-io.js";

  interface Props { children?: Snippet; }
  const { children }: Props = $props();
  const parent = useDeploymentIO();

  let inFlight = $state(false);
  let lastResult: unknown = $state(null);
  let lastError: Error | null = $state(null);

  async function dispatchOp(op: string, payload: unknown): Promise<unknown> {
    inFlight = true; lastError = null;
    try {
      const r = await parent.dispatch(op, payload);
      lastResult = r;
      return r;
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      lastError = err;
      throw err;
    } finally { inFlight = false; }
  }

  const ctx: QueueIOContext = {
    get inFlight() { return inFlight; },
    get lastResult() { return lastResult; },
    get lastError() { return lastError; },
    get history() { return parent.stream.filter((e) => e.op.startsWith("queue.")); },
    provision: (target: DeployTargetRef, options?) => dispatchOp("queue.provision", { target, options }),
    connect: (target, options?) => dispatchOp("queue.connect", { target, options }),
    publish: (target, message) => dispatchOp("queue.publish", { target, message }),
    peek: (target, options?) => dispatchOp("queue.peek", { target, options }),
    purge: (target, options?) => dispatchOp("queue.purge", { target, options }),
    drain: (target, options?) => dispatchOp("queue.drain", { target, options }),
    teardown: (target, options?) => dispatchOp("queue.teardown", { target, options }),
  } satisfies QueueIOContext;

  setContext(QUEUE_IO_KEY, ctx);
</script>
{@render children?.()}
